import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import clx from 'classnames';
import { Check, ChevronDown, LogOut } from 'lucide-react';
import { getSalons, getActiveSalon, getIsSwitchingSalon } from 'Containers/Auth/store/selectors';
import { authActions } from 'Containers/Auth/store';
import AvatarIcon from 'Containers/Common/AvatarIcon';
import { useTranslation } from 'react-i18next';
import { sendMessageToApp } from 'utils/exsternalCall';
import styles from './SalonSelector.module.scss';

const selectSalons = getSalons();
const selectActiveSalon = getActiveSalon();
const selectIsSwitchingSalon = getIsSwitchingSalon();

const buildPanelStyle = (triggerEl, { isCompact, isHeader }) => {
  if (!triggerEl) return {};

  const rect = triggerEl.getBoundingClientRect();
  const panelWidth = isCompact ? 280 : Math.max(rect.width, isHeader ? 260 : 260);
  const gap = 8;

  if (isCompact) {
    let left = rect.right + gap;
    if (left + panelWidth > window.innerWidth - gap) {
      left = rect.left - panelWidth - gap;
    }
    return {
      top: Math.max(gap, rect.top),
      left,
      width: panelWidth,
    };
  }

  const left = Math.min(
    rect.left,
    Math.max(gap, window.innerWidth - panelWidth - gap),
  );

  return {
    top: rect.bottom + gap,
    left,
    width: panelWidth,
  };
};

const panelStyleEquals = (a = {}, b = {}) =>
  a.top === b.top && a.left === b.left && a.width === b.width;

const SalonSelector = ({ isCompact = false, variant = 'default', className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const rafRef = useRef(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState({});
  const salons = useSelector(selectSalons);
  const activeSalon = useSelector(selectActiveSalon);
  const isSwitchingSalon = useSelector(selectIsSwitchingSalon);
  const activeCity = (activeSalon?.city || '').trim();
  const isHeader = variant === 'header';
  const nameLength = String(activeSalon?.name || '').trim().length;
  const headerSize =
    nameLength <= 10 ? 'short' : nameLength <= 20 ? 'medium' : 'long';

  const [, setSearchParams] = useSearchParams({ logout: 0 });

  const updatePanelPosition = useCallback(() => {
    const next = buildPanelStyle(triggerRef.current, { isCompact, isHeader });
    setPanelStyle((prev) => (panelStyleEquals(prev, next) ? prev : next));
  }, [isCompact, isHeader]);

  const schedulePanelPosition = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      updatePanelPosition();
    });
  }, [updatePanelPosition]);

  useEffect(() => {
    if (!isDropdownOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsDropdownOpen(false);
    };
    const handleClickOutside = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        panelRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsDropdownOpen(false);
    };

    window.addEventListener('resize', schedulePanelPosition);
    window.addEventListener('scroll', schedulePanelPosition, true);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      window.removeEventListener('resize', schedulePanelPosition);
      window.removeEventListener('scroll', schedulePanelPosition, true);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, schedulePanelPosition]);

  const toggleDropdown = (event) => {
    event.stopPropagation();
    if (isSwitchingSalon) return;
    if (isDropdownOpen) {
      setIsDropdownOpen(false);
      return;
    }
    setPanelStyle(buildPanelStyle(triggerRef.current, { isCompact, isHeader }));
    setIsDropdownOpen(true);
  };

  const selectSalon = (salonId) => {
    if (salonId === -1) {
      setSearchParams({ logout: 1 });
      sendMessageToApp('app:logout');
      return;
    }
    if (+salonId === +activeSalon?.value) {
      setIsDropdownOpen(false);
      return;
    }
    dispatch(authActions.SET_ACTIVE_SALON.REQUEST(salonId));
    setIsDropdownOpen(false);
  };

  const panel = isDropdownOpen
    ? createPortal(
        <div
          ref={panelRef}
          className={styles.salon_panel}
          style={panelStyle}
          role="listbox"
          aria-label={t('sideBar.salonSelector.title')}
        >
          <div className={styles.salon_panel_header}>
            <span className={styles.salon_panel_title}>
              {t('sideBar.salonSelector.title')}
            </span>
            <span className={styles.salon_panel_count}>
              {(salons || []).length}
            </span>
          </div>

          <ul className={styles.salon_list}>
            {(salons || []).map((salon) => {
              const isActive = salon.value === activeSalon?.value;
              const city = (salon.city || '').trim();
              return (
                <li key={salon.value} className={styles.salon_list_item}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    className={clx(styles.salon_option, {
                      [styles.salon_option_active]: isActive,
                    })}
                    onClick={() => selectSalon(salon.value)}
                  >
                    <AvatarIcon
                      icon={salon.icon}
                      name={salon.name}
                      size="36px"
                      lazy
                    />
                    <span className={styles.salon_option_copy}>
                      <span className={styles.salon_option_name}>{salon.name}</span>
                      {city ? <span className={styles.salon_option_city}>{city}</span> : null}
                    </span>
                    {isActive ? (
                      <Check
                        className={styles.salon_option_check}
                        size={16}
                        strokeWidth={2.5}
                        aria-hidden
                      />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>

          <div className={styles.salon_panel_divider} aria-hidden />

          <button
            type="button"
            className={styles.salon_logout}
            onClick={() => selectSalon(-1)}
          >
            <LogOut size={16} strokeWidth={2} aria-hidden />
            <span>{t('common.actions.logout')}</span>
          </button>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <div
        ref={triggerRef}
        className={clx(styles.salon_wrapper, className, {
          [styles.salon_wrapper_compact]: isCompact,
          [styles.salon_wrapper_header]: isHeader,
          [styles.salon_wrapper_header_short]: isHeader && headerSize === 'short',
          [styles.salon_wrapper_header_medium]: isHeader && headerSize === 'medium',
          [styles.salon_wrapper_header_long]: isHeader && headerSize === 'long',
          [styles.salon_wrapper_open]: isDropdownOpen,
          [styles.salon_wrapper_loading]: isSwitchingSalon,
        })}
      >
        <button
          type="button"
          className={styles.salon_trigger}
          onClick={toggleDropdown}
          disabled={isSwitchingSalon}
          aria-busy={isSwitchingSalon}
          aria-expanded={isDropdownOpen}
          aria-haspopup="listbox"
          aria-label={t('sideBar.salonSelector.changeSalon')}
          title={
            activeCity
              ? `${activeSalon?.name || ''} · ${activeCity}`
              : activeSalon?.name
          }
        >
          {isSwitchingSalon ? (
            <span className={styles.salon_loading_overlay} aria-hidden>
              <span className={styles.salon_loading_spinner} />
            </span>
          ) : null}
          {!isCompact ? (
            <AvatarIcon
              icon={activeSalon?.icon}
              name={activeSalon?.name}
              size={isHeader ? '28px' : '28px'}
            />
          ) : null}
          {isCompact ? (
            <>
              <span className={styles.salon_compact_stack}>
                <AvatarIcon
                  icon={activeSalon?.icon}
                  name={activeSalon?.name}
                  size="32px"
                />
                {activeCity ? (
                  <span className={styles.salon_city_compact} title={activeCity}>
                    {activeCity}
                  </span>
                ) : null}
              </span>
              <ChevronDown
                className={styles.salon_chevron_compact}
                size={12}
                strokeWidth={2.5}
                aria-hidden
              />
            </>
          ) : (
            <>
              <span className={styles.salon_trigger_text}>
                {!isHeader ? (
                  <span className={styles.salon_label}>
                    {t('sideBar.salonSelector.currentSalon')}
                  </span>
                ) : null}
                <span className={styles.salon_name}>{activeSalon?.name}</span>
                {activeCity ? (
                  <span className={styles.salon_city}>{activeCity}</span>
                ) : null}
              </span>
              <ChevronDown
                className={styles.salon_chevron}
                size={isHeader ? 14 : 16}
                strokeWidth={2}
                aria-hidden
              />
            </>
          )}
        </button>
      </div>
      {panel}
    </>
  );
};

export default SalonSelector;
